import { IconPen, IconTrash } from "@aksara-ui/icons";
import {
  Badge,
  Box,
  Card,
  IconButton,
  Table,
  TableBody,
  TableBodyCell,
  TableBodyRow,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  Text,
  theme,
} from "@aksara-ui/react";
import TableBodyRowsSkeleton from "components/TableBodyRowSkeleton";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import { getAllWarranties } from "redux/actions/warranty";
import styled from "styled-components";
import { WarrantyConfirmationModal, WarrantyFormModal } from "./components";
import {
  getAllTenantsFilter,
  setModalConfirmationOpen,
  setModalFormOpen,
} from "redux/actions";
import moment from "moment";
import { InputSearchbox } from "components/Form/InputSearchbox";
import {
  Pagination,
  PaginationDetail,
  PaginationFilter,
} from "components/Pagination";
import FilterKit from "components/FilterKit";
import { getRole } from "hooks";

const HeaderWrapper = styled(Box)`
  justify-content: space-between;
  margin-bottom: 30px;
  gap: 20px;
`;

const WarrantiesContent: React.FC = () => {
  const dispatch = useDispatch();
  const userRole = getRole();
  const isMobile = useMediaQuery({ maxWidth: 576 });
  const isTablet = useMediaQuery({ maxWidth: 1024 });

  const { isLoading, isModalFormOpen, isModalConfirmationOpen } = useSelector(
    (state: any) => state.globalReducer
  );
  const { warranties } = useSelector((state: any) => state.warrantyReducer);
  const { currentPage, totalPage, limitPage, lengthPage, filterTenantPage } =
    useSelector((state: any) => state.warrantyReducer);
  const { tenantsFilter } = useSelector((state: any) => state.tenantReducer);

  const [searchValue, setSearchValue] = useState("");
  const [limit, setLimit] = useState(10);
  const [selectedWarrantyId, setSelectedWarrantyId] = useState("");

  const handleSearchChange = (event: any) => {
    setSearchValue(event.target.value);
    dispatch(
      getAllWarranties(
        1,
        event.target.value,
        limitPage,
        filterTenantPage
      ) as any
    );
  };

  const handleClearSearchChange = () => {
    setSearchValue("");
    dispatch(
      getAllWarranties(currentPage, "", limitPage, filterTenantPage) as any
    );
  };

  const handleOpenFormModal = (warrantyId: string = "") => {
    dispatch(setModalFormOpen(true) as any);
    setSelectedWarrantyId(warrantyId);
  };

  const handleCloseFormModal = () => {
    dispatch(setModalFormOpen(false) as any);
    setSelectedWarrantyId("");
  };

  const handleOpenConfirmModal = (warrantyId: string) => {
    dispatch(setModalConfirmationOpen(true) as any);
    setSelectedWarrantyId(warrantyId);
  };

  const handleCloseConfirmModal = () => {
    dispatch(setModalConfirmationOpen(false) as any);
    setSelectedWarrantyId("");
  };

  useEffect(() => {
    dispatch(getAllTenantsFilter() as any);
    dispatch(getAllWarranties() as any);
  }, [dispatch]);

  return (
    <Box height={"100%"} paddingY={20} paddingX={isMobile ? 30 : 50}>
      <HeaderWrapper
        display={"flex"}
        flexDirection={"column"}
        style={{ gap: 20 }}
      >
        <Box display={"flex"}>
          <Text scale={[500, 600]} fontWeight={700}>
            Warranty Management
          </Text>
        </Box>

        <Box
          display={"flex"}
          flexDirection={isTablet ? "column" : "row"}
          style={{ gap: 5 }}
        >
          <InputSearchbox
            autoComplete="off"
            name="textDummy"
            placeholder="Search..."
            size={32}
            onChange={handleSearchChange}
            onClearButton={handleClearSearchChange}
          />

          {userRole !== "tenant" ? (
            <FilterKit
              orderByOptions={[
                {
                  key: tenantsFilter,
                  label: "Tenant",
                  options: tenantsFilter.map((tenant: any) => {
                    return {
                      label: tenant.name,
                      value: tenant.id,
                    };
                  }),
                },
              ]}
              onChangeOrder={(e: any) => {
                dispatch(
                  getAllWarranties(
                    1,
                    "",
                    limitPage,
                    e[tenantsFilter] ? e[tenantsFilter].value : ""
                  ) as any
                );
              }}
            />
          ) : (
            <></>
          )}
        </Box>
      </HeaderWrapper>

      <Card overflow={"auto"} elevation={2} maxHeight={"100%"}>
        <Table>
          <TableHead position={"sticky"} top={0} backgroundColor={"white"}>
            <TableHeadRow>
              <TableHeadCell>Key</TableHeadCell>
              <TableHeadCell>Device Code</TableHeadCell>
              <TableHeadCell>Expiry</TableHeadCell>
              <TableHeadCell>Approved</TableHeadCell>
              {/* <TableHeadCell>User</TableHeadCell> */}
              <TableHeadCell>Action</TableHeadCell>
            </TableHeadRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableBodyRowsSkeleton rowsNumber={10} columnsNumber={6} />
            ) : !warranties.length ? (
              <TableBodyRow>
                <TableBodyCell colSpan={5} textAlign={"center"}>
                  No data available in table
                </TableBodyCell>
              </TableBodyRow>
            ) : (
              warranties.map((warranty: any) => {
                return (
                  <TableBodyRow key={warranty.id}>
                    <TableBodyCell>{warranty.key}</TableBodyCell>
                    <TableBodyCell>{warranty.device_code}</TableBodyCell>
                    <TableBodyCell>
                      {warranty.warranty_expiry
                        ? moment(warranty.warranty_expiry).format("DD MMM YYYY")
                        : "-"}
                    </TableBodyCell>
                    <TableBodyCell whiteSpace={"nowrap"}>
                      <Badge
                        size="md"
                        variant={
                          warranty.is_approved === 1 ? "success" : "critical"
                        }
                      >
                        {warranty.is_approved === 1 ? "Yes" : "No"}
                      </Badge>
                    </TableBodyCell>
                    {/* <TableBodyCell>{warranty.user.name}</TableBodyCell> */}
                    <TableBodyCell>
                      <IconButton
                        onClick={() => handleOpenFormModal(warranty.id)}
                      >
                        <IconPen fill={theme.colors.green06} />
                      </IconButton>
                      <IconButton
                        onClick={() => handleOpenConfirmModal(warranty.id)}
                      >
                        <IconTrash fill={theme.colors.red06} />
                      </IconButton>
                    </TableBodyCell>
                  </TableBodyRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}
        paddingY={10}
      >
        {!isMobile ? (
          <PaginationDetail
            page={currentPage}
            limit={limitPage}
            length={lengthPage}
          />
        ) : (
          ""
        )}
        <Pagination
          current={currentPage}
          total={totalPage}
          onSelect={(select: any) =>
            dispatch(
              getAllWarranties(
                select,
                searchValue,
                limitPage,
                filterTenantPage
              ) as any
            )
          }
        />
        <PaginationFilter
          selectedItem={limit}
          onChange={(selectedItem) => {
            if (selectedItem) {
              setLimit(selectedItem);
              dispatch(
                getAllWarranties(
                  1,
                  searchValue,
                  selectedItem,
                  filterTenantPage
                ) as any
              );
            }
          }}
          items={[10, 20, 30, 40, 50]}
        />
      </Box>

      <WarrantyFormModal
        warrantyId={selectedWarrantyId}
        isOpen={isModalFormOpen}
        onClose={() => handleCloseFormModal()}
      />

      <WarrantyConfirmationModal
        warrantyId={selectedWarrantyId}
        isOpen={isModalConfirmationOpen}
        onClose={() => handleCloseConfirmModal()}
      />
    </Box>
  );
};

export default WarrantiesContent;
